<template>
  <div>
    <page-header
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <board class="mb-4" :details="generalInfoBoard" />
    <custom-tab v-if="loaded" :default-active-tab="activeTab">
      <custom-tab-item
        v-for="(tab, key) in tabs"
        :key="key"
        :title="tab.title"
        :lazy="false"
        :has-error="!tab.valid"
        @click="setCurrentTab(key)"
      >
        <component
          :is="tab.id"
          :ref="tab.id"
          :data="tab.data"
          :weeks="tab.weeks"
          :is-read-only="isReadOnly"
          v-model="tab.model"
          @next="tab.next($event)"
          @checkValidation="checkValidation()"
          @removedDocuments="onRemovedDocuments($event)"
        ></component>
      </custom-tab-item>
    </custom-tab>
  </div>
</template>

<script>
import peiTransitionStudentStatus from '@/utils/constants/pei/peiTransitionStudentStatus.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import PageHeader from 'PageHeader';
import Board from 'Board';
import { date } from '@/utils/filters.js';
import objectToFormData from 'object-to-formdata';
import transitionStudentService from '@/services/pei/transitionStudent.js';
import signatureService from '@/modules/common/signature.api';
import CustomError from 'custom-error';
import { CustomTab, CustomTabItem } from '@/components/common/custom-tabs';
import _ from 'lodash';
const GESTUAL_ANSWER_ID = 418;
const BRAILLE_ANSWER_ID = 413;

import {
  StudentInfo,
  AvailableEvaluations,
  StudentCapacity,
  ReasonableAccommodations,
  FunctionalSkills,
  Signatures,
} from './components';

const ROUTE_NAME_BACK = '/pei/transition-student';
const ROUTE_NAME_EGRESS = 'AddEditEgress';

export default {
  name: 'Main',
  mixins: [alertMixin],
  components: {
    PageHeader,
    Board,
    CustomError,
    CustomTab,
    CustomTabItem,
    StudentInfo,
    AvailableEvaluations,
    StudentCapacity,
    ReasonableAccommodations,
    FunctionalSkills,
    Signatures,
  },
  mixins: [alertMixin],
  data: () => ({
    page: {
      icon: 'fas fa-user-edit',
      title:
        'Certificación de Destrezas Académicas, Funcionales y Ocupacionales',
      routes: [
        'Inicio',
        'PEI',
        'SAEE-12 Certificación de destrezas académicas, funcionales y ocupacionales',
      ],
    },
    activeTab: 0,
    detail: {},
    loaded: false,
    languages: [],
    signatureCounter: 0,
    isFromEgress: false,
    removedDocuments: [],
  }),
  async mounted() {
    this.isFromEgress = this.$route.params.isFromEgress;
    await this.initData();
    this.loaded = true;
  },
  computed: {
    isReadOnly() {
      return this.detail.status === peiTransitionStudentStatus.Sometido;
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.goBack(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: !this.isReadOnly,
          click: () => this.handleSubmit(false),
        },
        {
          text: 'Someter',
          icon: 'fas fa-flag',
          variant: 'outline-success',
          visible: !this.isReadOnly,
          click: () => this.handleSubmit(),
        },
      ];
    },
    generalInfoBoard() {
      return [
        [
          {
            label: 'Año Académico:',
            icon: 'fas fa-calendar-alt',
            data: this.detail.generalInfo?.academicYear,
            cols: 'col-12 col-md-2',
          },
          {
            label: 'Nombre del Estudiante:',
            icon: 'fas fa-address-book',
            data: `${this.detail.generalInfo?.studentSieId} - ${this.detail.generalInfo?.studentName}`,
            cols: 'col-12 col-md-3',
          },
          {
            label: 'Fecha de Nacimiento:',
            icon: 'fas fa-calendar-day',
            data: date(this.detail.generalInfo?.dob),
            cols: 'col-12 col-md-2',
          },
          {
            label: 'Impedimento:',
            icon: 'fas fa-ballot-check',
            data: this.detail.generalInfo?.impediment,
            cols: 'col-12 col-md-2',
          },
          {
            label: 'Escuela:',
            icon: 'fas fa-school',
            data: `${this.detail.generalInfo?.schoolCode} - ${this.detail.generalInfo?.schoolName}`,
            cols: 'col-12 col-md-3',
          },
        ],
      ];
    },
    id() {
      const { id } = this.$route.params;
      return id ? Number(id) : null;
    },
    tabs() {
      return [
        {
          id: 'StudentInfo',
          title: 'Información del Estudiante',
          valid: true,
          data: {
            languages: [],
            languageId: null,
            email: null,
            dateDiscussion: null,
            diploma: null,
          },
          model: {
            language: null,
            email: null,
            dateDiscussion: null,
            diploma: null,
          },
          next: () => this.next(),
        },
        {
          id: 'AvailableEvaluations',
          title: 'Evaluaciones Disponibles',
          valid: true,
          data: { studentSieId: null, areas: [], readOnly: false },
          model: {},
          next: () => this.next(),
        },
        {
          id: 'StudentCapacity',
          title: 'Capacidades del Estudiante',
          valid: true,
          data: { questionaire: [] },
          model: { answers: [] },
          next: () => this.next(),
        },
        {
          id: 'ReasonableAccommodations',
          title: 'Acomodos Razonables',
          valid: true,
          data: {},
          model: {},
          next: () => this.next(),
        },
        {
          id: 'FunctionalSkills',
          title: 'Destrezas Funcionales',
          valid: true,
          data: { studentSieId: null },
          model: {},
          weeks: [4, 3],
          next: () => this.next(),
        },
        {
          id: 'Signatures',
          title: 'Firmas',
          valid: true,
          data: {
            availableSignatureOptions: [],
            participationTypeList: [],
            alternateParticipationTypeList: [],
            signatureInfoMessage: '',
            signatures: [],
            comment: null,
          },
          model: {
            hasComment: false,
            comment: '',
            signatureList: [],
            termsAccepted: false,
          },
          next: () => this.next(),
        },
      ];
    },
  },
  methods: {
    onRemovedDocuments(removedDocuments) {
      this.removedDocuments = removedDocuments;
    },
    async initData() {
      await this.getSaee12();
      await this.getAvailableSignatureOptions();
      await this.getParticipationTypeList();
      await this.getAlternateParticipationTypeList();
      await this.setSignatureConfig();
    },
    setCurrentTab(tab) {
      this.activeTab = tab;
    },
    async checkValidation() {
      const refs = this.tabs.map(({ id }) => this.$refs[id][0]);
      const validations = refs.map(({ validate }) => validate());
      const results = await Promise.allSettled(validations);
      results.forEach(({ value }, i) => (this.tabs[i].valid = value.valid));
      this.$forceUpdate();
    },
    async validate() {
      const refs = this.tabs.map(({ id }) => this.$refs[id][0]);
      const validations = refs.map(({ validate }) => validate());
      const results = await Promise.allSettled(validations);
      results.forEach(({ value }, i) => (this.tabs[i].valid = value.valid));
      this.$forceUpdate();
      const areValid = results.every(({ value }, i) => {
        const { valid, message } = value;
        if (!valid)
          this.ShowToast(`Error en ${this.tabs[i].title}`, message, 'error');
        return valid;
      });
      return areValid;
    },
    async getSaee12() {
      try {
        const { data } = await transitionStudentService.getSaee12(this.id);
        this.detail = data;
        this.tabs[0].data = {
          ...data.studentInformation.studentInfo,
          languages: data.studentInformation.languages,
        };
        this.tabs[1].data = {
          areas: data.availableEvaluations.areas.map((x) => {
            return {
              ...x,
              valid: true,
              displayed: false,
              title: x.subAreaName
                ? `${x.areaName}: ${x.subAreaName}`.trim()
                : x.areaName,
            };
          }),
          studentSieId: this.detail?.generalInfo.studentSieId,
          transitionStudentId: this.id,
        };
        this.tabs[2].data = data.studentCapacity;
        this.tabs[3].data = data.reasonableAccommodations;
        this.tabs[4].data.studentSieId = this.detail?.generalInfo.studentSieId;
        this.tabs[5].data.comment = this.detail?.comment;
      } catch (err) {
        this.ShowToast(
          'Error',
          err?.response?.data?.message ?? err?.message,
          'error'
        );
      }
    },
    async next() {
      if (!this.isReadOnly) await this.handleSave();
      else this.activeTab = this.activeTab + 1;
    },
    async handleSave() {
      await this.handleSubmit(false, true);
    },
    goBack() {
      if (this.isFromEgress) {
        this.$router.push({ name: ROUTE_NAME_EGRESS });
        return;
      }

      this.$router.push(ROUTE_NAME_BACK);
    },
    async handleSubmit(submitted = true, nextTab = false) {
      if (submitted) {
        const valid = await this.validate();
        if (!valid) return;
      }
      const models = this.tabs.map(({ model }) => model);
      models[2].answers.forEach((answer) => {
        if ([GESTUAL_ANSWER_ID, BRAILLE_ANSWER_ID].includes(answer.answerId)) {
          const select = document.querySelector(
            `#levels-select-${answer.answerId}-${answer.questionId}`
          );
          answer.otherAnswerNumber = Number(select.value);
        }
      });
      const studentInformation = {
        languageId: models[0].language?.id,
        email: models[0].email,
        dateDiscussion: models[0].dateDiscussion,
        diploma: models[0].diploma,
      };
      const signatures = models[5].signatureList
        .map((e) => ({
          groupId: e.groupId,
          fullName: e.signature.name,
          peiSignaturePositionCodeId:
            e.signature.positionId?.signaturePositionId,
          participationTypeId: e.signature.participationId?.id,
          alternateParticipationTypeId: e.signature.alternateId?.id,
          isRemovable: e.isRemovable,
        }))
        .filter(
          (x) =>
            Boolean(x.fullName) &&
            Boolean(x.participationTypeId) &&
            Boolean(x.peiSignaturePositionCodeId)
        );
      const payload = {
        id: this.id,
        isSubmit: submitted,
        termsAccepted: models[5].termsAccepted,
        comment: models[5].hasComment ? models[5].comment : null,
        studentInformation,
        studentCapacity: models[2],
        reasonableAccommodations: models[3],
        functionalSkills: models[4],
        signatures,
      };
      const formData = objectToFormData(payload, { indices: true });

      // documents that are being deleted or added during this submission
      const changedDocuments =
        models[1].backupDocumentsData?.flatMap((x) =>
          x.files
            .filter((file) => file instanceof File || file.newlySelected)
            // get the file and the details
            .map((file) => {
              const index = x.files.indexOf(file);
              // return the file and the details
              return {
                file,
                details: {
                  ...x.types[index],
                  areaId: x.areaId,
                  subAreaId: x.subAreaId,
                },
              };
            })
        ) ?? [];
      // add the removed documents to the changedDocuments array to be deleted from the server
      this.removedDocuments?.forEach((document) => {
        // this already contains the file and the details { file, details }
        changedDocuments.push(document);
      });

      changedDocuments.forEach((document, index) => {
        const keyName = `backUpDocuments[${index}]`;
        // new file
        const isNewFile = document.file instanceof File && document.file.name;
        if (isNewFile) {
          formData.append(`${keyName}.attachment`, document.file);
          formData.append(`${keyName}.fileName`, document.file?.name);
        }

        const fileDetails = document.details;

        // if the file is being deleted, send the id to the server to delete it from db
        if (document?.file?.deleteFromServer === true && document?.file?.id) {
          formData.append(
            `${keyName}.deleteFromServer`,
            document?.file?.deleteFromServer === true
          );
          formData.append(`${keyName}.DocumentIdToDelete`, document?.file?.id);
        }

        formData.append(
          `${keyName}.documentDate`,
          fileDetails?.documentRawDate
        );
        formData.append(
          `${keyName}.evaluationDate`,
          fileDetails?.documentRawDate
        );
        formData.append(
          `${keyName}.disciplineTypeName`,
          fileDetails?.disciplineTypeName
        );
        formData.append(
          `${keyName}.disciplineTypeId`,
          fileDetails?.disciplineTypeId
        );
        formData.append(
          `${keyName}.documentTypeId`,
          fileDetails?.documentTypeId
        );
        formData.append(`${keyName}.comment`, fileDetails?.comment);
        formData.append(`${keyName}.administrator`, fileDetails?.administrator);
        formData.append(
          `${keyName}.serviceTypeName`,
          fileDetails?.serviceTypeName
        );

        const areaId = fileDetails.areaId;
        const subAreaId = fileDetails.subAreaId;

        formData.append(`${keyName}.areaId`, areaId);
        formData.append(`${keyName}.subAreaId`, subAreaId);

        formData.append(
          `${keyName}.studentSieId`,
          this.detail.generalInfo?.studentSieId
        );
      });

      await transitionStudentService.submitSAEE12(formData);
      this.ShowToast(
        'Proceso Exitoso.',
        'La información se ha guardado correctamente.',
        'success'
      );

      // reset the removed documents and new files added
      this.removedDocuments = [];
      models[1].backupDocumentsData?.forEach((x) => {
        const toRemove = (x.files ?? []).reduce((acc, file, idx) => {
          if (file instanceof File || file.newlySelected) acc.push(idx);
          return acc;
        }, []);
        toRemove.reverse().forEach((idx) => {
          x.files.splice(idx, 1);
          x.types.splice(idx, 1);
        });

        x.files.forEach((file) => {
          file.deleteFromServer = false;
          delete file.newlySelected;
        });
      });

      if (submitted) {
        await this.initData();
      } else {
        await this.getSaee12();
        await this.setSignatureConfig();
      }
      if (nextTab) {
        this.activeTab = this.activeTab + 1;
      }
    },
    async getAvailableSignatureOptions() {
      const {
        data,
      } = await transitionStudentService.getAvailableSignatureOptions();
      this.tabs[5].data.availableSignatureOptions = data;
    },
    async getParticipationTypeList() {
      const {
        data,
      } = await transitionStudentService.getParticipationTypeList();
      this.tabs[5].data.participationTypeList = data;
    },
    async getAlternateParticipationTypeList() {
      const {
        data,
      } = await transitionStudentService.getAlternateParticipationTypeList();
      this.tabs[5].data.alternateParticipationTypeList = data;
    },
    async setSignatureConfig() {
      const requiredSignatures = [1, 4, 5];
      this.tabs[5].model.termsAccepted = this.detail.termsAccepted;
      this.tabs[5].data.signatures = this.detail.signatures;
      if (Boolean(this.detail.comment)) {
        this.tabs[5].model.hasComment = true;
        this.tabs[5].model.comment = this.detail.comment;
      }
      let signatureConfig = [];
      await signatureService
        .getDefaultConfiguration(
          this.detail.peiId,
          this.detail.generalInfo?.studentSieId
        )
        .then((result) => {
          signatureConfig = result.data;
        });

      this.tabs[5].model.signatureList = [];

      if (signatureConfig.length > 0) {
        this.tabs[5].data.signatureInfoMessage = signatureConfig.find(
          (e) => e.name != ''
        )?.name;
        signatureConfig.forEach((config) => {
          let signature = this.getBasicSignature(config.id, config.value);
          if (signature != undefined) {
            this.tabs[5].model.signatureList.push(signature);
          }
        });
        if (this.detail.signatures?.length > 0) {
          this.detail.signatures.forEach((signature) => {
            let signatureConfig = this.tabs[5].model.signatureList.find(
              (x) => x.id == signature.id
            );
            if (signatureConfig === undefined) {
              this.tabs[5].model.signatureList.push({
                id: signature.id,
                signature: {
                  name: signature.fullName,
                  positionId: {
                    signaturePositionId: signature.peiSignaturePositionCodeId,
                  },
                  participationId: { id: signature.participationTypeId },
                  alternateId: { id: signature.alternateParticipationTypeId },
                },
                groupId: signature.groupId,
                isRequired: !signature.isRemovable,
                isRemovable: signature.isRemovable,
                isDraft: true,
                isDisabled: false,
                isBasic: true,
              });
            }
          });
        }
      } else {
        this.tabs[5].model.signatureList = [];

        if (this.detail.signatures?.length > 0) {
          requiredSignatures.forEach((groupId) => {
            let signature = this.getBasicSignature(groupId);
            if (
              signature != undefined &&
              this.detail.signatures.find(
                (e) => e.groupId == groupId && !e.isRemovable
              ) == undefined
            ) {
              this.tabs[5].model.signatureList.push(signature);
            }
          });
          this.tabs[5].model.signatureList.push(
            ...this.detail.signatures.map((e) => ({
              id: e.id,
              signature: {
                name: e.fullName,
                positionId: {
                  signaturePositionId: e.peiSignaturePositionCodeId,
                },
                participationId: { id: e.participationTypeId },
                alternateId: { id: e.alternateParticipationTypeId },
              },
              groupId: e.groupId,
              isRequired: !e.isRemovable,
              isRemovable: e.isRemovable,
              isDraft: true,
              isDisabled: false,
              isBasic: true,
            }))
          );
        } else {
          let signature1 = this.getBasicSignature(1);
          if (signature1 != undefined) {
            this.tabs[5].model.signatureList.push(signature1);
          }

          let signature5 = this.getBasicSignature(5);
          if (signature5 != undefined) {
            this.tabs[5].model.signatureList.push(signature5);
          }

          let signature4 = this.getBasicSignature(4);
          if (signature4 != undefined) {
            this.tabs[5].model.signatureList.push(signature4);
          }
        }
      }

      this.tabs[5].model.signatureList = _.orderBy(
        this.tabs[5].model.signatureList,
        ['isRemovable'],
        ['asc']
      );
    },
    getBasicSignature(groupId, removable = false) {
      let signature = this.detail.signatures.find((x) =>
        this.tabs[5].data.availableSignatureOptions.some(
          (y) =>
            y.signatureGroupId == groupId &&
            y.signaturePositionId == x.peiSignaturePositionCodeId
        )
      );
      if (signature != undefined) {
        return {
          id: signature.id,
          signature: {
            name: signature.fullName,
            positionId: {
              signaturePositionId: signature.peiSignaturePositionCodeId,
            },
            participationId: { id: signature.participationTypeId },
            alternateId: { id: signature.alternateParticipationTypeId },
          },
          groupId: groupId,
          isRequired: false,
          isRemovable: removable,
          isDraft: true,
          isDisabled: false,
          isBasic: true,
        };
      } else {
        this.signatureCounter += 1;
        return {
          id: this.signatureCounter,
          signature: {
            name: '',
            positionId: undefined,
            participationId: undefined,
            alternateId: undefined,
          },
          groupId: groupId,
          isRequired: false,
          isRemovable: removable,
          isBasic: true,
        };
      }
    },
  },
};
</script>
<style scoped lang="scss">
@import '@/assets/scss/variables/_colors.scss';
::v-deep .custom-tab .custom-tab-container {
  margin-bottom: 20px !important;
}
::v-deep .custom-tab .custom-tab-container .nav-item .nav-link {
  padding-right: 50px !important;
  padding-left: 30px !important;
}
::v-deep .custom-tab .custom-tab-container .nav-item .nav-link.active {
  border-bottom: 3px solid map-get($colors, 'orange') !important;
  color: map-get($colors, 'orange') !important;
  padding-right: 50px !important;
  padding-left: 30px !important;
}
</style>
